// @owners { team: patients }
import { COLORS } from '@alto/design-library-tokens';
import {
  Body,
  CenterView,
  Column,
  ExternalLink,
  H1,
  Illustration,
  LgPadding,
  LgSpacing,
  XsSpacing,
} from '@alto/design-system';
import * as React from 'react';
import { type ImageSourcePropType, Platform, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import styled from 'styled-components/native';
import gearsIllustration from '~shared/assets/images/gears.png';
import { IMG_ALT_TEXTS } from '~shared/constants';
import { isMobileWeb } from '~shared/helpers/helper';
import { stripNonDigits } from '~shared/helpers/string';

export type DowntimePageProps = {
  readonly title: string;
  /**
   * When `phoneNumber` is also provided, a "please call {phoneNumber}" sentence is appended
   * directly after this text — `message` should end with terminal punctuation (e.g. a period) in
   * that case so the combined copy doesn't read as a run-on sentence.
   */
  readonly message: string;
  readonly phoneNumber?: string;
  readonly onPressPhoneNumber?: () => void;
  readonly image?: ImageSourcePropType;
  readonly testID?: string;
};

const Background = styled(Column)`
  ${Platform.OS === 'web' && 'min-height: 100vh;'}
`;

const ContentWidth = styled(View)`
  flex: 1;
  ${Platform.OS === 'web' &&
  `
    max-width: 600px;
    align-self: center;
    width: 100%;
  `}
`;

const FullWidthPadding = styled(LgPadding)`
  align-items: center;
  width: 100%;
`;

type DowntimeDescriptionProps = Pick<DowntimePageProps, 'message' | 'phoneNumber' | 'onPressPhoneNumber'>;

const DowntimeDescription = ({ message, phoneNumber, onPressPhoneNumber }: DowntimeDescriptionProps) => {
  if (!phoneNumber) {
    return <>{message}</>;
  }

  // Use non breaking space to prevent phone number wrapping, same convention as PhoneNumber.tsx
  const phoneNumberDisplay = phoneNumber.replace(/ /g, '\u00a0');

  return (
    <>
      {message} If you need urgent pharmacy support, please call{' '}
      <ExternalLink
        href={`tel://${stripNonDigits(phoneNumber)}`}
        onPress={onPressPhoneNumber}
      >
        {phoneNumberDisplay}
      </ExternalLink>
      .
    </>
  );
};

/**
 * Cross-platform, non-dismissible full-screen page shown in place of normal app content during
 * scheduled downtime. Presentational only — the caller decides when to render it and supplies the
 * resolved title/message/phoneNumber.
 */
export const DowntimePage = ({
  title,
  message,
  phoneNumber,
  onPressPhoneNumber,
  image = gearsIllustration as ImageSourcePropType,
  testID,
}: DowntimePageProps) => {
  // This page is meant to be mounted above normal app navigation (replacing it entirely during
  // downtime), so it can't rely on an ancestor screen's own safe-area handling.
  const { top, bottom } = useSafeAreaInsets();
  // Smaller illustration on native and mobile web; full size is reserved for desktop web.
  const illustrationSize = Platform.OS !== 'web' || isMobileWeb() ? 'xl' : 'xxl';

  return (
    <Background
      backgroundColor={COLORS.BACKGROUND_COLORS.PRIMARY_LIGHTEST}
      flexGrow={1}
      flexShrink={1}
      style={{ paddingBottom: bottom, paddingTop: top }}
      testID={testID}
    >
      <ContentWidth>
        <CenterView>
          <FullWidthPadding>
            <Illustration
              accessibilityLabel={IMG_ALT_TEXTS.gears}
              size={illustrationSize}
              source={image}
            />
            <LgSpacing />
            <H1 center>{title}</H1>
            <XsSpacing />
            <Body>
              <DowntimeDescription
                message={message}
                onPressPhoneNumber={onPressPhoneNumber}
                phoneNumber={phoneNumber}
              />
            </Body>
          </FullWidthPadding>
        </CenterView>
      </ContentWidth>
    </Background>
  );
};
